import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { SYMPTOM_CATALOG } from '../../utils/wellness';
import './TodayView.css';

/* ── Helpers ── */

function getCurrentWeek() {
  const now = new Date();
  const day = now.getDay(); // 0 = Sun
  const sun = new Date(now);
  sun.setDate(now.getDate() - day);

  const labels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(sun);
    d.setDate(sun.getDate() + i);
    days.push({
      label: labels[i],
      dateStr: d.toISOString().split('T')[0],
      dateNum: d.getDate(),
      isToday: d.toDateString() === now.toDateString(),
      isPast: d < new Date(now.toDateString()),
    });
  }
  return days;
}

function ProgressRing({ pct, color }) {
  const size = 36;
  const r = 15;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="bubble-progress">
      <svg width={size} height={size} viewBox="0 0 36 36">
        <circle cx="18" cy="18" r={r} fill="none" stroke="var(--color-border, #e0e0e0)" strokeWidth="3" />
        <circle
          cx="18" cy="18" r={r} fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        />
      </svg>
      <span className="bubble-progress-text">{pct}%</span>
    </div>
  );
}

function getTimeBucket(time) {
  if (!time) return 'Anytime';
  const hour = parseInt(time.split(':')[0], 10);
  if (hour < 12) return 'Morning';
  if (hour < 17) return 'Afternoon';
  return 'Evening';
}

function getTimeLabel(timeBlock) {
  const map = { morning: 'Morning', midday: 'Mid-Day', evening: 'Evening', overall: 'All day' };
  return map[timeBlock] || '';
}

/* ── Check icon ── */
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* ── Component ── */

export default function TodayView() {
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  // Supplements
  const [supplements, setSupplements] = useState([]);
  const [suppCheckoffs, setSuppCheckoffs] = useState([]);

  // Protocol (to-do items)
  const [todoItems, setTodoItems] = useState([]);
  const [todoCheckoffs, setTodoCheckoffs] = useState([]);

  // Symptoms
  const [enabledSymptoms, setEnabledSymptoms] = useState([]);
  const [scores, setScores] = useState({});
  const [symptomSaving, setSymptomSaving] = useState(false);
  const [symptomSaved, setSymptomSaved] = useState(false);

  // Notes
  const [note, setNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);

  // Week
  const [weekActivity, setWeekActivity] = useState({});

  // UI
  const [openBubbles, setOpenBubbles] = useState(new Set());

  const today = new Date().toISOString().split('T')[0];
  const weekDays = getCurrentWeek();

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  async function loadData() {
    setLoading(true);

    const { data: clientData } = await supabase
      .from('clients')
      .select('*')
      .eq('auth_user_id', user.id)
      .single();

    if (!clientData) { setLoading(false); return; }
    setClient(clientData);

    if (clientData.enabled_symptoms && Array.isArray(clientData.enabled_symptoms)) {
      setEnabledSymptoms(clientData.enabled_symptoms);
    }

    const weekStart = weekDays[0].dateStr;
    const weekEnd = weekDays[6].dateStr;

    const [suppRes, suppCheckRes, todoRes, todoCheckRes, logRes, journalRes, wkSuppRes, wkTodoRes] = await Promise.all([
      supabase.from('supplements').select('*')
        .eq('client_id', clientData.id).eq('is_active', true)
        .order('reminder_time', { ascending: true, nullsFirst: false })
        .order('sort_order'),
      supabase.from('supplement_checkoffs').select('*')
        .eq('client_id', clientData.id).eq('date', today),
      supabase.from('todo_items').select('*')
        .eq('client_id', clientData.id).eq('is_active', true)
        .order('sort_order').order('created_at'),
      supabase.from('todo_checkoffs').select('*')
        .eq('client_id', clientData.id).eq('date', today),
      supabase.from('daily_logs').select('*')
        .eq('client_id', clientData.id).eq('date', today).limit(1),
      supabase.from('daily_journals').select('*')
        .eq('client_id', clientData.id).eq('date', today).limit(1),
      supabase.from('supplement_checkoffs').select('date')
        .eq('client_id', clientData.id).gte('date', weekStart).lte('date', weekEnd),
      supabase.from('todo_checkoffs').select('date')
        .eq('client_id', clientData.id).gte('date', weekStart).lte('date', weekEnd),
    ]);

    setSupplements(suppRes.data || []);
    setSuppCheckoffs(suppCheckRes.data || []);
    setTodoItems(todoRes.data || []);
    setTodoCheckoffs(todoCheckRes.data || []);

    // Restore symptom scores
    const existingLog = logRes.data?.[0];
    if (existingLog) {
      const restored = {};
      for (const s of SYMPTOM_CATALOG) {
        if (existingLog[s.key] != null) restored[s.key] = existingLog[s.key];
      }
      setScores(restored);
    }

    // Restore note
    const existingJournal = journalRes.data?.[0];
    if (existingJournal) setNote(existingJournal.journal_1 || '');

    // Week activity map
    const wk = {};
    (wkSuppRes.data || []).forEach(c => { wk[c.date] = (wk[c.date] || 0) + 1; });
    (wkTodoRes.data || []).forEach(c => { wk[c.date] = (wk[c.date] || 0) + 1; });
    setWeekActivity(wk);

    setLoading(false);
  }

  /* ── Supplement checkoff ── */
  async function toggleSuppCheckoff(supplementId) {
    const existing = suppCheckoffs.find(c => c.supplement_id === supplementId);
    if (existing) {
      await supabase.from('supplement_checkoffs').delete().eq('id', existing.id);
      setSuppCheckoffs(prev => prev.filter(c => c.id !== existing.id));
    } else {
      const { data } = await supabase
        .from('supplement_checkoffs')
        .insert({ client_id: client.id, supplement_id: supplementId, date: today })
        .select().single();
      if (data) setSuppCheckoffs(prev => [...prev, data]);
    }
  }

  /* ── Todo checkoff ── */
  async function toggleTodoCheckoff(itemId) {
    const existing = todoCheckoffs.find(c => c.todo_item_id === itemId);
    if (existing) {
      await supabase.from('todo_checkoffs').delete().eq('id', existing.id);
      setTodoCheckoffs(prev => prev.filter(c => c.id !== existing.id));
    } else {
      const { data } = await supabase
        .from('todo_checkoffs')
        .insert({ client_id: client.id, todo_item_id: itemId, date: today })
        .select().single();
      if (data) setTodoCheckoffs(prev => [...prev, data]);
    }
  }

  /* ── Save symptoms ── */
  async function saveSymptoms() {
    if (!client) return;
    setSymptomSaving(true);
    const logPayload = { client_id: client.id, date: today };
    for (const s of SYMPTOM_CATALOG) {
      logPayload[s.key] = scores[s.key] != null ? parseInt(scores[s.key]) : null;
    }
    const { error } = await supabase
      .from('daily_logs')
      .upsert(logPayload, { onConflict: 'client_id,date' });
    setSymptomSaving(false);
    if (error) {
      console.error('Symptom save error:', error);
      alert('Failed to save symptoms: ' + error.message);
    } else {
      setSymptomSaved(true);
      setTimeout(() => setSymptomSaved(false), 2000);
    }
  }

  /* ── Save note ── */
  async function saveNote() {
    if (!client) return;
    setNoteSaving(true);
    const { error } = await supabase
      .from('daily_journals')
      .upsert({
        client_id: client.id,
        date: today,
        journal_1: note || null,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'client_id,date' });
    setNoteSaving(false);
    if (error) {
      alert('Failed to save note: ' + error.message);
    } else {
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 2000);
    }
  }

  /* ── Bubble toggle ── */
  function toggleBubble(key) {
    setOpenBubbles(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  /* ── Computed ── */
  const suppTotal = supplements.length;
  const suppDone = suppCheckoffs.length;
  const suppPct = suppTotal > 0 ? Math.round((suppDone / suppTotal) * 100) : 0;

  const todoTotal = todoItems.length;
  const todoDone = todoCheckoffs.length;
  const todoPct = todoTotal > 0 ? Math.round((todoDone / todoTotal) * 100) : 0;

  const activeSymptoms = SYMPTOM_CATALOG.filter(s => enabledSymptoms.includes(s.key));
  const symptomTotal = activeSymptoms.length;
  const symptomDone = activeSymptoms.filter(s => scores[s.key] != null).length;
  const symptomPct = symptomTotal > 0 ? Math.round((symptomDone / symptomTotal) * 100) : 0;

  function getDayStatus(day) {
    if (day.isToday) return 'today';
    if (!day.isPast) return 'future';
    const count = weekActivity[day.dateStr] || 0;
    if (count === 0) return 'missed';
    const totalItems = suppTotal + todoTotal;
    if (totalItems > 0 && count >= totalItems) return 'complete';
    return count > 0 ? 'partial' : 'missed';
  }

  /* ── Loading / empty states ── */
  if (loading) {
    return (
      <div className="today-screen">
        <div className="today-loading">Loading your day...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="today-screen">
        <div className="today-loading">
          <h2>Welcome to Correly</h2>
          <p style={{ marginTop: 8, color: 'var(--color-text-secondary)' }}>
            Your practitioner hasn't set up your protocol yet. Check back soon!
          </p>
        </div>
      </div>
    );
  }

  const firstName = client.name.split(' ')[0];
  const dayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="today-screen">
      {/* Greeting */}
      <div className="today-greeting">
        <h1>Hi, {firstName}</h1>
        <p className="today-date">{dayLabel}</p>
      </div>

      {/* Week strip */}
      <div className="week-strip">
        {weekDays.map(day => {
          const status = getDayStatus(day);
          return (
            <div key={day.dateStr} className="week-day">
              <span className="week-day-label">{day.label}</span>
              <div className={`week-day-circle week-${status}`}>
                {status === 'complete' ? <CheckIcon /> : <span>{day.dateNum}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bubbles */}
      <div className="bubbles">

        {/* ── Supplements ── */}
        {suppTotal > 0 && (
          <div className={`bubble ${openBubbles.has('supps') ? 'bubble-open' : ''}`}>
            <button className="bubble-header" onClick={() => toggleBubble('supps')}>
              <div className="bubble-icon bubble-icon-green">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="7" width="18" height="10" rx="2" /><path d="M12 7v10" />
                </svg>
              </div>
              <div className="bubble-info">
                <div className="bubble-title">Supplements</div>
                <div className="bubble-sub">{suppDone} of {suppTotal} taken</div>
              </div>
              <ProgressRing pct={suppPct} color="#3D7A5E" />
              <span className="bubble-chevron" />
            </button>
            {openBubbles.has('supps') && (
              <div className="bubble-body">
                <ul className="checklist">
                  {supplements.map(supp => {
                    const checked = suppCheckoffs.some(c => c.supplement_id === supp.id);
                    return (
                      <li key={supp.id} onClick={() => toggleSuppCheckoff(supp.id)}>
                        <div className={`check-box ${checked ? 'checked' : ''}`}>
                          {checked && <CheckIcon />}
                        </div>
                        <span className={`item-text ${checked ? 'done' : ''}`}>
                          {supp.name}{supp.dosage ? ` — ${supp.dosage}` : ''}
                        </span>
                        <span className="item-detail">{getTimeBucket(supp.reminder_time)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ── Protocol ── */}
        {todoTotal > 0 && (
          <div className={`bubble ${openBubbles.has('protocol') ? 'bubble-open' : ''}`}>
            <button className="bubble-header" onClick={() => toggleBubble('protocol')}>
              <div className="bubble-icon bubble-icon-purple">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              </div>
              <div className="bubble-info">
                <div className="bubble-title">Protocol</div>
                <div className="bubble-sub">{todoDone} of {todoTotal} completed</div>
              </div>
              <ProgressRing pct={todoPct} color="#5C4A9E" />
              <span className="bubble-chevron" />
            </button>
            {openBubbles.has('protocol') && (
              <div className="bubble-body">
                <ul className="checklist">
                  {todoItems.map(item => {
                    const checked = todoCheckoffs.some(c => c.todo_item_id === item.id);
                    return (
                      <li key={item.id} onClick={() => toggleTodoCheckoff(item.id)}>
                        <div className={`check-box ${checked ? 'checked' : ''}`}>
                          {checked && <CheckIcon />}
                        </div>
                        <span className={`item-text ${checked ? 'done' : ''}`}>{item.name}</span>
                        <span className="item-detail">{getTimeLabel(item.time_block)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ── Symptoms ── */}
        {symptomTotal > 0 && (
          <div className={`bubble ${openBubbles.has('symptoms') ? 'bubble-open' : ''}`}>
            <button className="bubble-header" onClick={() => toggleBubble('symptoms')}>
              <div className="bubble-icon bubble-icon-amber">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19.5 12.572l-7.5 7.428l-7.5-7.428a5 5 0 1 1 7.5-6.566a5 5 0 1 1 7.5 6.572" />
                </svg>
              </div>
              <div className="bubble-info">
                <div className="bubble-title">Symptoms</div>
                <div className="bubble-sub">{symptomDone} of {symptomTotal} rated</div>
              </div>
              <ProgressRing pct={symptomPct} color="#B07D4B" />
              <span className="bubble-chevron" />
            </button>
            {openBubbles.has('symptoms') && (
              <div className="bubble-body">
                <div className="symptom-list">
                  {activeSymptoms.map(symptom => {
                    const val = scores[symptom.key];
                    const hasValue = val != null;
                    return (
                      <div key={symptom.key} className="symptom-row">
                        <div className="symptom-top">
                          <span className="symptom-label">{symptom.label}</span>
                          <span className="symptom-val">{hasValue ? val : '—'}</span>
                        </div>
                        <div className="slider-wrap">
                          <span className="slider-anchor">1 Bad</span>
                          <input
                            type="range"
                            className="symptom-slider"
                            min="1" max="5" step="1"
                            value={hasValue ? val : 3}
                            onChange={e => {
                              setSymptomSaved(false);
                              setScores(prev => ({ ...prev, [symptom.key]: parseInt(e.target.value) }));
                            }}
                          />
                          <span className="slider-anchor slider-right">5 Great</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button className="bubble-save" onClick={saveSymptoms} disabled={symptomSaving}>
                  {symptomSaving ? 'Saving...' : symptomSaved ? '✓ Saved' : 'Save Symptoms'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Notes ── */}
        <div className={`bubble ${openBubbles.has('notes') ? 'bubble-open' : ''}`}>
          <button className="bubble-header" onClick={() => toggleBubble('notes')}>
            <div className="bubble-icon bubble-icon-coral">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
                <line x1="9" y1="13" x2="15" y2="13" />
                <line x1="9" y1="17" x2="15" y2="17" />
              </svg>
            </div>
            <div className="bubble-info">
              <div className="bubble-title">Notes to practitioner</div>
              <div className="bubble-sub">Share anything on your mind</div>
            </div>
            <span className="bubble-chevron" />
          </button>
          {openBubbles.has('notes') && (
            <div className="bubble-body">
              <textarea
                className="notes-area"
                rows={4}
                placeholder="How are you feeling? Anything your practitioner should know..."
                value={note}
                onChange={e => { setNoteSaved(false); setNote(e.target.value); }}
              />
              <button className="bubble-save notes-send" onClick={saveNote} disabled={noteSaving}>
                {noteSaving ? 'Saving...' : noteSaved ? '✓ Sent' : 'Send note'}
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
